import { z } from "zod";
import {
  CodeSchemeKeySchema,
  ShortCodeEntityTypeSchema,
} from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import { OPTIONAL_TEXT_INPUT } from "../procurement/optionalInputs";

/**
 * Import Short Codes
 *
 * Takes ONE entity's code under ONE Code Scheme as single-value sockets and
 * makes it the entity's active code under that scheme, retiring the code it
 * replaces. Generic over entity type: config names the type and the scheme by
 * key, so a published workflow version holds no scheme ids. An ingest with
 * codes under several schemes uses one node per scheme.
 *
 * Runs AFTER the step that creates the entities (for warehouses,
 * `upsert_warehouse`), in the same two-pass shape as
 * `upsert_warehouse_hierarchy`: `entityKey` is looked up, never created. A
 * warehouse is found by its origin id, a division by its id.
 *
 * A code that does not fit the scheme is stored as nonconforming, not
 * rejected. A value another entity holds, or that two rows of a batch claim,
 * goes to no one. Rerunning the same rows changes nothing.
 */
export const importShortCodesStep = defineStepConfig({
  type: "import_short_codes",
  name: "Import Short Codes",
  description:
    "Makes each row's value the entity's active Short Code under the configured Code Scheme, retiring the code it replaces. Entities are looked up by source key (warehouse origin id, division id), never created. Codes that do not fit the scheme are kept as nonconforming; a value another entity holds is reported as a conflict.",
  category: "Transformation",
  libraryModule: "Platform",

  inputSchema: z.object({
    entityKey: z
      .string()
      .describe(
        "How the source names the entity: a warehouse's origin id, or a division's id",
      ),
    value: OPTIONAL_TEXT_INPUT.describe(
      "The code as the source writes it, matched exactly. Blank skips the row.",
    ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["inserted", "unchanged", "reactivated", "conflict", "skipped"])
      .describe("What the import did with the row"),
    conforms: z
      .boolean()
      .nullable()
      .describe(
        "Whether the stored code fits its scheme; null when nothing was stored",
      ),
    reason: z
      .string()
      .nullable()
      .describe(
        "Why a row was skipped or conflicted: empty_value, value_too_long, entity_not_found, value_held_by_other_entity, value_claimed_twice_in_batch or entity_given_two_values_in_batch",
      ),
  }),

  configSchema: z.object({
    entityType: ShortCodeEntityTypeSchema.describe(
      "The kind of entity the codes belong to",
    ),
    scheme: CodeSchemeKeySchema.describe(
      "Key of the Code Scheme the codes are issued under",
    ),
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
